import { useMemo, useState } from 'react'
import { PlusCircle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getCategories } from '../../services/CategoryService'
import { useDeleteProduct, useProducts } from '../../hooks/useProducts'
import ProductCard from '../../components/products/ProductCard'
import SearchBar from '../../components/products/SearchBar'
import ProductFormModal from '../../components/products/ProductFormModal'
import ProductViewModal from '../../components/products/ProductViewModal'
import ConfirmDeleteModal from '../../components/products/ConfirmDeleteModal'
import type { Product, ProductCardVM } from '../../types/product'

export default function ProductsPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isFormOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingProductId, setEditingProductId] = useState<string | number | null>(null)
  const [viewingProductId, setViewingProductId] = useState<string | number | null>(null)
  const [deleteProductId, setDeleteProductId] = useState<string | number | null>(null)

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  })

  const { data: products = [], isPending, isError } = useProducts(debouncedSearch)
  const deleteMutation = useDeleteProduct()

  const productCards = useMemo<ProductCardVM[]>(() => {
    const normalized = debouncedSearch.trim().toLowerCase()
    const list = (Array.isArray(products) ? products : []) as Product[]
    const filtered = normalized
      ? list.filter((product) => {
        const haystack = [
          product.name,
          product.description,
          product.category?.name ?? '',
          product.ingredients.map((ingredient) => ingredient.name).join(' '),
        ]
          .join(' ')
          .toLowerCase()
        return haystack.includes(normalized)
      })
      : list

    return filtered.map((product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      categoryLabel: product.category?.name ?? 'Sin categoría',
      isActive: product.active,
      imageUrl: product.imageUrl,
      ingredients: product.ingredients.map((ingredient) => ingredient.name),
    }))
  }, [products, debouncedSearch])

  const openCreateModal = () => {
    setFormMode('create')
    setEditingProductId(null)
    setFormOpen(true)
  }

  const openEditModal = (id: string | number) => {
    setFormMode('edit')
    setEditingProductId(id)
    setFormOpen(true)
  }

  const closeFormModal = () => {
    setFormOpen(false)
    setEditingProductId(null)
  }

  const closeViewModal = () => {
    setViewingProductId(null)
  }

  const closeDeleteModal = () => {
    setDeleteProductId(null)
  }

  const handleConfirmDelete = async () => {
    if (!deleteProductId) return
    await deleteMutation.mutateAsync(deleteProductId)
    setDeleteProductId(null)
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900 dark:text-neutral-100">Productos</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Gestiona tu catálogo en modo claro u oscuro sin perder consistencia.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
        >
          <PlusCircle className="h-5 w-5" />
          Agregar producto
        </button>
      </div>
      <SearchBar
        value={search}
        onChange={setSearch}
        onDebouncedChange={setDebouncedSearch}
        placeholder="Busca por nombre, descripción o ingrediente"
      />
      {isPending ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando productos…</p>
      ) : isError ? (
        <p className="text-sm text-rose-500">No pudimos cargar los productos. Intenta nuevamente.</p>
      ) : productCards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No encontramos productos para tu búsqueda.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {productCards.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              highlight={debouncedSearch}
              onView={(id) => setViewingProductId(id)}
              onEdit={openEditModal}
              onDelete={(id) => setDeleteProductId(id)}
            />
          ))}
        </div>
      )}
      <ProductFormModal
        mode={formMode}
        isOpen={isFormOpen}
        onClose={closeFormModal}
        productId={formMode === 'edit' ? editingProductId : undefined}
        categories={categories}
      />
      <ProductViewModal isOpen={Boolean(viewingProductId)} productId={viewingProductId} onClose={closeViewModal} />
      <ConfirmDeleteModal
        isOpen={Boolean(deleteProductId)}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        productName={products.find((product) => String(product.id) === String(deleteProductId))?.name}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  )
}
